// ============================================================
// document-scanner : détection des bords d'un document (partagé)
// ============================================================
// Coeur RÉUTILISABLE du détourage de documents, utilisé par :
//   - le scanner de frais du portail salarié (scan-fee-screen)
//   - (à venir) les rachats clients (facture d'achat + pièce d'identité)
//   - (à venir) l'inbox back-office (tickets/factures du dirigeant)
//
// Détection principale : U²-Net (segmentation d'objet entraînée, ONNX, exécutée
// 100% dans le navigateur via onnxruntime-web). C'est l'approche des scanners
// natifs (Samsung/Adobe Scan) : on segmente le DOCUMENT, pas ses bords, ça
// tient même sur fond chargé, ticket froissé ou blanc-sur-blanc, là où la
// vision classique (Canny/contours) échoue.
//
// OpenCV.js (pour le redressement perspective via jscanify) et le modèle ONNX
// sont chargés à la demande. Tout est en repli « sans faille » : si un maillon
// ne charge pas (réseau mobile capricieux), la détection renvoie null et l'appli
// retombe sur le recadrage manuel : la feature ne casse jamais.
// ============================================================

export interface Corner { x: number; y: number }
export interface Quad { tl: Corner; tr: Corner; br: Corner; bl: Corner }

export interface JscanifyInstance {
  findPaperContour: (mat: unknown) => unknown;
  getCornerPoints: (contour: unknown) => {
    topLeftCorner: Corner; topRightCorner: Corner; bottomLeftCorner: Corner; bottomRightCorner: Corner;
  };
  extractPaper: (image: HTMLImageElement | HTMLCanvasElement, w: number, h: number, corners?: unknown) => HTMLCanvasElement;
}

interface OrtTensor { data: Float32Array | Uint8Array | number[] }
interface OrtSession {
  inputNames: string[];
  outputNames: string[];
  run: (feeds: Record<string, unknown>) => Promise<Record<string, OrtTensor>>;
}
// Type opaque de session de modèle exposé aux appelants (portail, rachats, inbox).
export type DocModelSession = OrtSession;
interface OrtNamespace {
  env: { wasm: { wasmPaths: string; numThreads: number } };
  Tensor: new (type: string, data: Float32Array, dims: number[]) => unknown;
  InferenceSession: { create: (path: string, opts?: unknown) => Promise<OrtSession> };
}

declare global {
  interface Window {
    cv?: { Mat?: unknown; imread: (el: unknown) => unknown; onRuntimeInitialized?: () => void } & Record<string, any>;
    jscanify?: new () => JscanifyInstance;
    ort?: OrtNamespace;
  }
}

// URL du modèle servie en MÊME ORIGINE (apps/web/public/models) : pas de CORS,
// mis en cache par le navigateur après le premier chargement (~4,5 Mo).
const MODEL_URL = '/models/u2netp.onnx';
const ORT_CDN = 'https://cdn.jsdelivr.net/npm/onnxruntime-web/dist/';

// ── Chargeurs (singletons module-level) ──

function injectScript(id: string, src: string, ready: () => boolean): Promise<void> {
  return new Promise((resolve, reject) => {
    if (ready()) { resolve(); return; }
    const existing = document.getElementById(id) as HTMLScriptElement | null;
    const onload = () => (ready() ? resolve() : reject(new Error(id + ' chargé mais absent')));
    if (existing) { existing.addEventListener('load', onload); existing.addEventListener('error', () => reject(new Error(id + ' load failed'))); return; }
    const s = document.createElement('script');
    s.id = id; s.src = src; s.async = true;
    s.onload = onload;
    s.onerror = () => reject(new Error(id + ' load failed'));
    document.head.appendChild(s);
  });
}

let cvPromise: Promise<void> | null = null;
export function loadOpenCv(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('SSR'));
  if (window.cv?.Mat) return Promise.resolve();
  if (cvPromise) return cvPromise;
  cvPromise = new Promise((resolve, reject) => {
    const finish = () => {
      const cv = window.cv;
      if (!cv) { reject(new Error('cv absent')); return; }
      if (cv.Mat) { resolve(); return; }
      cv.onRuntimeInitialized = () => resolve();
    };
    const s = document.createElement('script');
    s.id = 'opencv-js';
    // OpenCV embarqué par jscanify (version compatible garantie) servi par jsdelivr.
    s.src = 'https://cdn.jsdelivr.net/npm/jscanify@1.3.0/src/opencv.js';
    s.async = true;
    s.onload = finish;
    s.onerror = () => { cvPromise = null; reject(new Error('opencv load failed')); };
    document.head.appendChild(s);
  });
  return cvPromise;
}

let jscanifyPromise: Promise<void> | null = null;
export function loadJscanify(): Promise<void> {
  jscanifyPromise ??= injectScript('jscanify-js', 'https://cdn.jsdelivr.net/npm/jscanify@1.3.0/src/jscanify.js', () => !!window.jscanify)
    .catch((e) => { jscanifyPromise = null; throw e; });
  return jscanifyPromise;
}
export function newJscanify(): JscanifyInstance | null {
  try { return window.jscanify ? new window.jscanify() : null; } catch { return null; }
}

let ortPromise: Promise<void> | null = null;
function loadOrt(): Promise<void> {
  ortPromise ??= injectScript('onnxruntime-web', ORT_CDN + 'ort.min.js', () => !!window.ort)
    .catch((e) => { ortPromise = null; throw e; });
  return ortPromise;
}

let modelPromise: Promise<OrtSession | null> | null = null;
export function loadDocModel(): Promise<OrtSession | null> {
  modelPromise ??= (async () => {
    await loadOrt();
    const ort = window.ort!;
    ort.env.wasm.wasmPaths = ORT_CDN;
    ort.env.wasm.numThreads = 1; // mono-thread : robuste (pas de worker cross-origin)
    return ort.InferenceSession.create(MODEL_URL, { executionProviders: ['wasm'] });
  })().catch((e) => { modelPromise = null; throw e; });
  return modelPromise;
}

// ── Détection ML (U²-Net) ──

function cornersFromPoints(pts: Corner[]): Quad {
  const sum = pts.map((p) => p.x + p.y), diff = pts.map((p) => p.x - p.y);
  return {
    tl: pts[sum.indexOf(Math.min(...sum))],
    tr: pts[diff.indexOf(Math.max(...diff))],
    br: pts[sum.indexOf(Math.max(...sum))],
    bl: pts[diff.indexOf(Math.min(...diff))],
  };
}

// Segmente le document avec U²-Net puis en extrait les 4 coins. Renvoie null si
// le modèle n'est pas chargé ou si rien de probant n'est trouvé. Le garde-fou de
// plausibilité (isPlausibleQuad) est laissé à l'appelant pour décider du repli.
export async function detectDocumentQuad(session: OrtSession, img: HTMLImageElement | HTMLCanvasElement): Promise<Quad | null> {
  // cv en any : l'API OpenCV.js (Mat, morphologyEx, findContours…) n'est pas typée.
  const cv: any = window.cv; // eslint-disable-line @typescript-eslint/no-explicit-any
  const ort = window.ort;
  if (!cv || !ort) return null;
  const SZ = 320;
  const c = document.createElement('canvas'); c.width = SZ; c.height = SZ;
  const cctx = c.getContext('2d'); if (!cctx) return null;
  cctx.drawImage(img, 0, 0, SZ, SZ);
  const data = cctx.getImageData(0, 0, SZ, SZ).data;
  // Prétraitement U²-Net : /max puis normalisation ImageNet, format NCHW.
  let maxv = 1;
  for (let i = 0; i < data.length; i += 4) maxv = Math.max(maxv, data[i], data[i + 1], data[i + 2]);
  const mean = [0.485, 0.456, 0.406], std = [0.229, 0.224, 0.225], plane = SZ * SZ;
  const f = new Float32Array(3 * plane);
  for (let p = 0, j = 0; p < plane; p++, j += 4) {
    f[p] = ((data[j] / maxv) - mean[0]) / std[0];
    f[plane + p] = ((data[j + 1] / maxv) - mean[1]) / std[1];
    f[2 * plane + p] = ((data[j + 2] / maxv) - mean[2]) / std[2];
  }
  const input = new ort.Tensor('float32', f, [1, 3, SZ, SZ]);
  const feeds: Record<string, unknown> = {}; feeds[session.inputNames[0]] = input;
  const out = await session.run(feeds);
  const od = out[session.outputNames[0]].data as Float32Array;

  let mn = Infinity, mx = -Infinity;
  for (let i = 0; i < plane; i++) { if (od[i] < mn) mn = od[i]; if (od[i] > mx) mx = od[i]; }
  const rng = (mx - mn) || 1;

  let quad: Quad | null = null;
  const mask = new cv.Mat(SZ, SZ, cv.CV_8UC1);
  const k = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(5, 5));
  const contours = new cv.MatVector();
  const hier = new cv.Mat();
  try {
    for (let i = 0; i < plane; i++) mask.data[i] = ((od[i] - mn) / rng) > 0.5 ? 255 : 0;
    cv.morphologyEx(mask, mask, cv.MORPH_CLOSE, k);
    cv.morphologyEx(mask, mask, cv.MORPH_OPEN, k);
    cv.findContours(mask, contours, hier, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);
    let best = -1, bestA = 0;
    for (let i = 0; i < contours.size(); i++) { const a = cv.contourArea(contours.get(i)); if (a > bestA) { bestA = a; best = i; } }
    if (best >= 0 && bestA > 0.03 * plane) {
      const cnt = contours.get(best);
      const peri = cv.arcLength(cnt, true);
      let pts: Corner[] | null = null;
      for (const e of [0.02, 0.03, 0.04, 0.05, 0.06, 0.08]) {
        const ap = new cv.Mat();
        cv.approxPolyDP(cnt, ap, e * peri, true);
        if (ap.rows === 4) { pts = []; for (let j = 0; j < 4; j++) pts.push({ x: ap.data32S[j * 2], y: ap.data32S[j * 2 + 1] }); ap.delete(); break; }
        ap.delete();
      }
      if (!pts) { // masque ML propre : l'enveloppe convexe ne « bave » pas comme en CV pure
        const hull = new cv.Mat();
        cv.convexHull(cnt, hull, false, true);
        pts = []; for (let j = 0; j < hull.rows; j++) pts.push({ x: hull.data32S[j * 2], y: hull.data32S[j * 2 + 1] });
        hull.delete();
      }
      if (pts.length >= 4) {
        const fx = (img instanceof HTMLImageElement ? img.naturalWidth : img.width) / SZ;
        const fy = (img instanceof HTMLImageElement ? img.naturalHeight : img.height) / SZ;
        quad = cornersFromPoints(pts.map((p) => ({ x: p.x * fx, y: p.y * fy })));
      }
    }
  } catch {
    quad = null;
  } finally {
    mask.delete(); k.delete(); contours.delete(); hier.delete();
  }
  return quad;
}

// ── Détection jscanify (repli / redressement perspective) ──

export function detectQuadJscanify(scanner: JscanifyInstance, el: HTMLImageElement | HTMLCanvasElement): Quad | null {
  const cv = window.cv;
  if (!cv) return null;
  let mat: unknown = null;
  try {
    mat = cv.imread(el);
    const contour = scanner.findPaperContour(mat);
    if (!contour) return null;
    const c = scanner.getCornerPoints(contour);
    if (!c?.topLeftCorner || !c?.bottomRightCorner) return null;
    const q: Quad = { tl: c.topLeftCorner, tr: c.topRightCorner, br: c.bottomRightCorner, bl: c.bottomLeftCorner };
    if (Math.abs(q.tr.x - q.tl.x) < 20 || Math.abs(q.bl.y - q.tl.y) < 20) return null;
    return q;
  } catch {
    return null;
  } finally {
    // @ts-expect-error delete() existe sur les Mat opencv
    if (mat && typeof mat.delete === 'function') mat.delete();
  }
}

// ── Redressement perspective (aplatissement) ──

function dist(a: Corner, b: Corner): number { return Math.hypot(a.x - b.x, a.y - b.y); }

// Redresse le document délimité par `quad` (coords image naturelle) en un
// rectangle bien à plat, via transformation de perspective OpenCV BRUTE.
// IMPORTANT : on n'utilise PAS jscanify.extractPaper(), qui relance sa propre
// détection interne (findPaperContour) avant de redresser et plante sur pas mal
// de photos → l'app retombait alors sur un recadrage rectangulaire qui gardait
// l'arrière-plan (bug user 19/06/2026 : "je vois l'arrière-plan, pas d'aplati").
// Ici on warpe directement avec les 4 coins fournis → aplati fiable et net.
export function warpDocument(img: HTMLImageElement | HTMLCanvasElement, quad: Quad): HTMLCanvasElement | null {
  const cv: any = window.cv; // eslint-disable-line @typescript-eslint/no-explicit-any
  if (!cv) return null;
  const wEst = Math.round((dist(quad.tl, quad.tr) + dist(quad.bl, quad.br)) / 2);
  const hEst = Math.round((dist(quad.tl, quad.bl) + dist(quad.tr, quad.br)) / 2);
  const w = Math.max(600, wEst), h = Math.max(600, hEst);
  let src: any = null, dst: any = null, M: any = null, srcTri: any = null, dstTri: any = null; // eslint-disable-line @typescript-eslint/no-explicit-any
  try {
    src = cv.imread(img);
    dst = new cv.Mat();
    srcTri = cv.matFromArray(4, 1, cv.CV_32FC2, [quad.tl.x, quad.tl.y, quad.tr.x, quad.tr.y, quad.bl.x, quad.bl.y, quad.br.x, quad.br.y]);
    dstTri = cv.matFromArray(4, 1, cv.CV_32FC2, [0, 0, w, 0, 0, h, w, h]);
    M = cv.getPerspectiveTransform(srcTri, dstTri);
    cv.warpPerspective(src, dst, M, new cv.Size(w, h), cv.INTER_LINEAR, cv.BORDER_CONSTANT, new cv.Scalar());
    const canvas = document.createElement('canvas');
    cv.imshow(canvas, dst);
    return canvas;
  } catch {
    return null;
  } finally {
    [src, dst, M, srcTri, dstTri].forEach((m) => { try { if (m && m.delete) m.delete(); } catch { /* noop */ } });
  }
}

// ── Garde-fou de plausibilité (partagé) ──

// Rejette les détections aberrantes : aire raisonnable (12% à 98%), coins dans
// le cadre, quadrilatère pas trop « tordu » ni trop en biais. Sert de garde-fou
// commun ML + jscanify : une forme non plausible (ex : une main captée par le
// ML) est écartée pour retomber sur le rectangle centré neutre.
export function isPlausibleQuad(q: Quad, w: number, h: number): boolean {
  const pts = [q.tl, q.tr, q.br, q.bl];
  for (const p of pts) {
    if (!p || p.x < -2 || p.y < -2 || p.x > w + 2 || p.y > h + 2) return false;
  }
  let area = 0;
  for (let i = 0; i < 4; i++) { const a = pts[i], b = pts[(i + 1) % 4]; area += a.x * b.y - b.x * a.y; }
  area = Math.abs(area) / 2;
  const ratio = area / (w * h);
  if (ratio < 0.12 || ratio > 0.98) return false;

  const top = dist(q.tl, q.tr), bottom = dist(q.bl, q.br);
  const left = dist(q.tl, q.bl), right = dist(q.tr, q.br);
  if (Math.max(top, bottom) / Math.max(1, Math.min(top, bottom)) > 1.6) return false;
  if (Math.max(left, right) / Math.max(1, Math.min(left, right)) > 1.6) return false;

  const bboxW = Math.max(q.tl.x, q.tr.x, q.br.x, q.bl.x) - Math.min(q.tl.x, q.tr.x, q.br.x, q.bl.x);
  const bboxH = Math.max(q.tl.y, q.tr.y, q.br.y, q.bl.y) - Math.min(q.tl.y, q.tr.y, q.br.y, q.bl.y);
  const fill = area / Math.max(1, bboxW * bboxH);
  if (fill < 0.70) return false; // trop en biais (ex : main en triangle) -> rectangle centré

  return true;
}
